import './firebase';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { GlobalStyles, paperClasses } from '@mui/material';
import { TrackerProvider } from '@dpdgroupuk/react-event-tracker';
import { PersistGate } from 'redux-persist/integration/react';

import React from 'react';
import axe from 'react-axe';
import { sessionApis, DpdThemeProvider } from '@dpdgroupuk/cc-dpd-ui';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import './index.scss';
import '@dpdgroupuk/cc-chat-fe/dist/components/ChatBotApp.css';
import { AUTH_BASE_URL } from './constants/env';

import { persistor, store } from './redux/store';
import tracker from './helpers/analytics';
import { ErrorBoundary } from './features/ErrorBoundary';

// Initialize cc-dpd-ui session API client (used by AuthBridge/session APIs)
sessionApis.getSessionApiClientInstance(`${AUTH_BASE_URL}/auth`);
import AuthProvider from './features/Auth/AuthProvider';

// Override parcelStatus colours that differ from the shared theme, and ensure
// outlined inputs use the theme's paper background so they adapt to dark mode.
const themeOptions = {
  colorSchemes: {
    light: {
      palette: {
        parcelStatus: {
          'In Transit': '#E65100',
        },
      },
    },
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--mui-palette-background-paper)',
          '&:hover:not(.Mui-disabled, .Mui-error)': {
            backgroundColor: 'var(--mui-palette-background-paper)',
          },
          '&.Mui-focused': {
            backgroundColor: 'var(--mui-palette-background-paper)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.getColorSchemeSelector('dark')]: {
            backgroundImage: 'none',
            [`&:not(.${paperClasses.outlined})`]: {
              border: `1px solid ${theme.vars.palette.shadowBorder.main}`,
            },
          },
        }),
      },
    },
  },
};

const rootElement = document.getElementById('root');
const root = createRoot(rootElement);
if (import.meta.env.NODE_ENV !== 'production') {
  axe(React, root, 1000);
}
root.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <TrackerProvider tracker={tracker}>
        <DpdThemeProvider themeOptions={themeOptions}>
          <GlobalStyles
            styles={{
              'input:-webkit-autofill': {
                boxShadow:
                  '0 0 0 1000px var(--mui-palette-background-paper) inset !important',
                WebkitTextFillColor:
                  'var(--mui-palette-text-primary) !important',
                transition: 'background-color 5000s ease-in-out 0s',
              },
            }}
          />
          <ErrorBoundary>
            <BrowserRouter>
              <AuthProvider>
                <App />
              </AuthProvider>
            </BrowserRouter>
          </ErrorBoundary>
        </DpdThemeProvider>
      </TrackerProvider>
    </PersistGate>
  </Provider>
);
